import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { CheckBadgeIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { selectUser } from "../../../store/slices/auth";
import { useSelector } from "../../../store/store";
import { Step, VoiceClonerData } from "../VoiceClonerModal";
import { AudioSampleUploader } from "./AudioSampleUploader";
import { cloneVoice } from "../../../api/voices";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  onNext: (data: Partial<VoiceClonerData>) => void;
  steps: Step[];
};

export type LocalFile = File & { url: string; name: string };

export const CustomVoiceEditor: FC<Props> = ({ open, setOpen, onNext }) => {
  const { t } = useTranslation();
  const advises = [
    {
      title: t("audioBook.adviseTitle1"),
      description: t("audioBook.adviseDescription1"),
    },
    {
      title: t("audioBook.adviseTitle2"),
      description: t("audioBook.adviseDescription2"),
    },
    {
      title: t("audioBook.adviseTitle3"),
      description: t("audioBook.adviseDescription3"),
    },
  ];
  const [files, setFiles] = useState<LocalFile[]>([]);
  const user = useSelector(selectUser);

  type CustomVoiceFormData = {
    voiceType: "Custom";
    customVoiceId: string;
    customVoiceName: string;
  };

  const CustomVoiceFormSchema = Yup.object().shape({
    voiceType: Yup.string()
      .oneOf(["Custom"], t("errors.required"))
      .required(t("errors.required")),
    customVoiceId: Yup.string().required(t("errors.required")),
    customVoiceName: Yup.string().required(t("errors.required")),
  });

  const { setValue } = useForm<CustomVoiceFormData>({
    defaultValues: {
      voiceType: "Custom",
      customVoiceId: "",
      customVoiceName: "",
    },
    resolver: yupResolver(CustomVoiceFormSchema),
  });

  const createVoice = async () => {
    const voice = await cloneVoice(files);
    const voiceName = user?.first_name + " " + user?.last_name;
    setValue("customVoiceId", voice.voice_id);
    setValue("customVoiceName", voiceName);
    onNext({
      voiceType: "Custom",
      customVoiceId: voice.voice_id,
      customVoiceName: voiceName,
    });
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto flex items-center justify-center p-2 sm:p-4">
        <div className="flex h-full max-h-screen items-center justify-center text-center w-full">
          <DialogPanel
            transition
            className="h-full max-h-full relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-3 pb-3 pt-4 sm:px-4 sm:pb-4 sm:pt-5 text-left w-full sm:w-5/6 sm:max-w-5xl shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="absolute right-0 top-0 pr-3 pt-3 sm:hidden sm:pr-4 sm:pt-4">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-5 sm:size-6" />
              </button>
            </div>
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <DialogTitle
                  as="h3"
                  className="text-2xl font-semibold text-slate-800"
                >
                  {t("audioBook.CreateMyOwnVoice")}
                </DialogTitle>
                <hr className="mt-2 w-full border-gray-200" />
              </div>
            </div>
            <div className="flex flex-col lg:flex-row mt-4 lg:mt-5 justify-around h-full overflow-hidden">
              <div className="flex flex-col w-full lg:w-3/12 space-y-3 lg:space-y-4 lg:border-r lg:border-rust-200 lg:pr-4 mb-4 lg:mb-0">
                {advises.map((advise) => (
                  <div className="flex flex-col" key={advise.title}>
                    <div className="flex flex-row">
                      <div className="w-1/6">
                        <CheckBadgeIcon className="h-5 w-5 lg:h-6 lg:w-6 text-herb-500" />
                      </div>
                      <div className="w-5/6">
                        <h3 className="text-sm lg:text-normal font-semibold text-delicate-800">
                          {advise.title}
                        </h3>
                      </div>
                    </div>
                    <div className="flex flex-row">
                      <div className="w-1/6"></div>
                      <div className="w-5/6">
                        <p className="text-xs lg:text-sm text-slate-600">
                          {advise.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col w-full lg:w-9/12 lg:mr-6 overflow-y-auto h-full">
                <AudioSampleUploader
                  files={files}
                  setFiles={setFiles}
                  createVoice={createVoice}
                />
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
